import { GetInitialDataCtx, WidgetProps } from "../../contracts/widget"
import { exploreWidgetConfig, ExploreWidgetConfig, VisitorFilter } from "./config"
import { WidgetProvider } from "../../providers/widget"
import { ExplorerProvider } from "./providers"
import { ExplorerContent } from "./components/ExplorerContent"
import "./explorer.css"
import { useMemo } from "react"
import { parseWidgetConfig } from "@homerunner-next/widget-core/schema"

type ExplorerWidgetData = {
  initialFilters?: Partial<VisitorFilter>
}

// The playground preview iframe is the only host where the page URL is written
// solely by our own filter sync (it never receives visitor deep links) AND
// where live edits remount the provider. Re-parsing that self-written URL on a
// remount latched stale values — un-checking "Featured Only" could never take
// effect (#837). In the preview, defaults always win; on real embed pages the
// URL keeps full precedence (visitor deep links, SPA remounts).
const isPreviewHost =
  typeof window !== "undefined" &&
  window.location.pathname.endsWith("/widget-preview.html")

export const getInitialData = async ({ options, feedId, widgetId }: GetInitialDataCtx<ExploreWidgetConfig>) => {
  return {}
}

export default function ExplorerWidget({ options, feedId, widgetId, resolvedTheme, data }: WidgetProps<ExploreWidgetConfig, ExplorerWidgetData>) {
  // parseWidgetConfig strips null/'' leaves before parsing: Central nullifies
  // empty strings on save; a null string leaf must read as "absent", not crash
  // the parse (#858, promoted to widget-core in #1168).
  const parsedOptions = useMemo(
    () => parseWidgetConfig(exploreWidgetConfig, options),
    [options],
  )
  const initialFilters = useMemo(() => ({
    ...parsedOptions.visitorFilterDefaults,
    ...data?.initialFilters,
  }), [parsedOptions.visitorFilterDefaults, data?.initialFilters])
  // openMapOnMount only takes effect when showMap is enabled.
  const initialMapVisible = parsedOptions.showMap === true && parsedOptions.openMapOnMount === true
  // Key forces ExplorerProvider to remount when filter defaults or initial map
  // visibility change (e.g. playground config updates).
  const providerKey = useMemo(
    () => JSON.stringify({ initialFilters, initialMapVisible }),
    [initialFilters, initialMapVisible],
  )
  // Visitor toggle persistence scope — disabled in the playground preview
  // (live-preview toggles must not pollute the visitor store) and when the
  // map feature is off (a stored "map open" must not resurrect a disabled map).
  const visitorPrefsKey =
    parsedOptions.showMap === true && !isPreviewHost ? `${feedId}:${widgetId}` : null
  // Compare Homes (#1713) reads its settings through the provider tree it lives in.
  const compareHost = useMemo(
    () => ({ options: parsedOptions, feedId, resolvedTheme }),
    [parsedOptions, feedId, resolvedTheme],
  )
  return (
    <WidgetProvider options={parsedOptions} feedId={feedId} widgetId={widgetId} resolvedTheme={resolvedTheme} widgetType="explorer" data={data ?? {}}>
      <ExplorerProvider key={providerKey} initialFilters={initialFilters} initialMapVisible={initialMapVisible} ignoreUrlFilters={isPreviewHost} visitorPrefsKey={visitorPrefsKey} compare={compareHost}>
        <ExplorerContent />
      </ExplorerProvider>
    </WidgetProvider>
  )
}
